import { HEALTH } from "@/observability";
import { toDate } from "@/utils/adminDates";

/**
 * Derive a display-friendly Health value for a live admin upload row.
 *
 * The backend admin/active endpoint does NOT persist per-request health —
 * it's a client-side concept computed from state + timeline (see
 * observability/diagnostics/UploadHealthCalculator.js). For the admin live
 * page we don't have the client's timeline, so we approximate from the row
 * data we DO have:
 *   - status                    → COMPLETED / FAILED classification
 *   - pauseReason               → WAITING
 *   - watchdogGaveUp            → FAILED
 *   - watchdogAutoRestartCount  → RETRYING
 *   - updatedAt (idle window)   → POSSIBLY_STUCK
 *
 * Ordering follows severity so admins see the most alarming state first.
 */

const STUCK_IDLE_MS = 60_000;

export const deriveRowHealth = (row) => {
  if (!row) return HEALTH.HEALTHY;
  const status = row.status;

  if (
    status === "PUBLISHED" ||
    status === "UPLOADED" ||
    status === "EDITED" ||
    status === "TAGGED" ||
    status === "CAT_DV_INGESTED"
  ) {
    return HEALTH.COMPLETED;
  }
  if (status === "FAILED" || status === "REJECTED") {
    return HEALTH.FAILED;
  }
  // ON_HOLD joins PAUSED as WAITING: both are deliberate stops with a human on the
  // other end. Explicitly not COMPLETED (processing has not finished) and not stuck —
  // the idle-time check below would otherwise flag a multi-day hold as a problem.
  if (status === "PAUSED" || status === "ON_HOLD" || row.pauseReason) {
    return HEALTH.WAITING;
  }
  // Bug fix 2026-08: `watchdogGaveUp` alone must NOT mean HEALTH.FAILED.
  // The frontend stall watchdog gives up after 5 auto-restarts, but tus's
  // own onShouldRetry loop keeps trying underneath — and often succeeds
  // (real incident: watchdog gave up on file 2 at 39%, bytes still made it
  // to 89% over the next 3 hours). The correct label for "watchdog exhausted
  // but backend is still non-terminal" is Retrying, not Failed.
  if (row.watchdogGaveUp || (row.watchdogAutoRestartCount || 0) >= 3) {
    return HEALTH.RETRYING;
  }

  const updatedAt = toDate(row.updatedAt);
  if (updatedAt) {
    const idleMs = Date.now() - updatedAt.getTime();
    if (
      idleMs >= STUCK_IDLE_MS &&
      (status === "CHUNK_UPLOAD_IN_PROGRESS" || status === "PENDING_CHUNKS")
    ) {
      return HEALTH.POSSIBLY_STUCK;
    }
  }

  return HEALTH.HEALTHY;
};

export const HEALTH_LABEL = {
  [HEALTH.HEALTHY]: "Healthy",
  [HEALTH.SLOW]: "Slow",
  [HEALTH.RETRYING]: "Retrying",
  [HEALTH.WAITING]: "Waiting",
  [HEALTH.OFFLINE]: "Offline",
  [HEALTH.POSSIBLY_STUCK]: "Possibly stuck",
  [HEALTH.FAILED]: "Failed",
  [HEALTH.COMPLETED]: "Completed",
};

/**
 * Chip colour hint (MUI palette key). Kept outside the theme object so the
 * severity ordering can be tested without pulling in the theme.
 */
export const HEALTH_SEVERITY = {
  [HEALTH.HEALTHY]: "success",
  [HEALTH.COMPLETED]: "success",
  [HEALTH.WAITING]: "info",
  [HEALTH.SLOW]: "warning",
  [HEALTH.RETRYING]: "warning",
  [HEALTH.OFFLINE]: "warning",
  [HEALTH.POSSIBLY_STUCK]: "error",
  [HEALTH.FAILED]: "error",
};
